// v1.33: Dashboard 4-5 卡片细化 + Sticky 顶栏 (借鉴 GoodJob CRM)
// 设计目标: 1 人运营每天登进 Trade OS 第一眼看到的"该做什么"面板
//
// v1.32 → v1.33 改动:
//   - 4 大卡内容细化 (询盘/应收/订单/节日 各加 3-4 个细节)
//   - 加第 5 卡 (AI 教练入口) + 5 快捷操作缩为 2/3
//   - Sticky 顶栏: 系统状态 (4 服务点) + 3 快捷按钮
//   - 移动端: 5 卡片单列 + 顶栏按钮改汉堡弹层
//
// 数据源 (全部 v1.30/1.31/1.32 现成 API, 不动 endpoint):
//   GET /api/inquiries?status=new
//   GET /api/finance/ar/summary
//   GET /api/orders?status=producing
//   GET /api/holidays   + 计算到下一个节日
//   GET /api/coach/sessions (今日对话数)
//   GET /api/system/status (4 服务点)
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Icon } from '../components/Icon';
import { CardSkeleton } from '../components/Skeleton';
import { Badge, Button, Card } from '../components/ui';
import {
  type CoachSession,
  type Holiday,
  customerApi,
  financeApi,
  holidayApi,
  inquiryApi,
  orderApi,
  systemApi,
} from '../lib/api';
import { fmtMoneyCompact } from '../lib/format';

export const Route = createFileRoute('/')({
  component: Dashboard,
});

// v1.32: 导出 for unit test
export { Dashboard };
export {
  bucketOverdueAmountCents,
  bucketOverdueOrderCount,
  draftStatusForInquiry,
  formatRelativeTime,
  holidayEmojiFor,
  inquiryDraftStatus,
  monthlyDeltaSign,
  orderFactoryStages,
  orderPostshipStages,
  statusDotClass,
  todayStartMs,
};

// === 工具: 算下个节日还有几天 ===
function nextHolidayDiff(holidays: Holiday[]): { name: string; days: number; key: string } | null {
  if (holidays.length === 0) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const year = now.getFullYear();
  type Cand = { name: string; date: Date; key: string };
  const cands: Cand[] = [];
  for (const h of holidays) {
    if (h.dynamic) continue; // 动态节日 (春节/中秋) 暂不展开 — 留作后续 lunar 计算
    // 试今年 + 明年
    for (const y of [year, year + 1]) {
      const d = new Date(y, h.solarMonth - 1, h.solarDay);
      if (d.getTime() >= now.getTime()) {
        cands.push({ name: h.name.zh, date: d, key: h.key });
      }
    }
  }
  if (cands.length === 0) return null;
  cands.sort((a, b) => a.date.getTime() - b.date.getTime());
  const next = cands[0];
  if (!next) return null;
  const days = Math.ceil((next.date.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  return { name: next.name, days, key: next.key };
}

function greetingForDate(d: Date): string {
  const h = d.getHours();
  if (h < 6) return '夜深了';
  if (h < 11) return '早上好';
  if (h < 14) return '中午好';
  if (h < 18) return '下午好';
  return '晚上好';
}

/** 今日 0 点 ms (本地) */
function todayStartMs(d: Date = new Date()): number {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
}

/** v1.33: 询盘 → AI 起草状态 (从 inquiry.status 推断, 复用 v1.22 约定) */
function inquiryDraftStatus(
  status: 'new' | 'quoted' | 'sampled' | 'ordered' | 'rejected' | 'archived',
): 'undrafted' | 'drafted' | 'sent' {
  if (status === 'new') return 'undrafted';
  if (status === 'quoted' || status === 'sampled') return 'drafted';
  if (status === 'ordered') return 'sent';
  return 'drafted';
}

/** v1.33: 询盘状态 → 显示 label (复用 emoji 业务语义, 见 design-system §十二) */
function draftStatusForInquiry(s: 'undrafted' | 'drafted' | 'sent'): {
  label: string;
  icon: string;
  tone: 'neutral' | 'info' | 'success';
} {
  if (s === 'undrafted') return { label: '未起草', icon: '👀', tone: 'neutral' };
  if (s === 'drafted') return { label: '已起草', icon: '✅', tone: 'info' };
  return { label: '已发', icon: '📨', tone: 'success' };
}

/** v1.33: 订单 status 分类 — 工厂待发 (装船前) */
const FACTORY_STATUSES = new Set([
  'draft',
  'deposit_received',
  'producing',
  'qc',
  'balance_received',
]);

/** v1.33: 订单 status 分类 — 客户待回款 (装船后, 收尾款) */
const POSTSHIP_STATUSES = new Set(['shipped']);

/** v1.33: 工具 — 过滤工厂待发订单 (含 balance_received 是因为客户已付尾款, 工厂已收齐待装船) */
function orderFactoryStages<T extends { status: string }>(items: T[]): T[] {
  return items.filter((o) => FACTORY_STATUSES.has(o.status));
}

/** v1.33: 工具 — 过滤客户待回款订单 */
function orderPostshipStages<T extends { status: string }>(items: T[]): T[] {
  return items.filter((o) => POSTSHIP_STATUSES.has(o.status));
}

/** v1.33: AR 超期合计 (31-60 + 61-90 + 90+ cents) — 复用 v1.31 AR summary 字段 */
function bucketOverdueAmountCents(
  buckets: Record<string, { outstandingCents: number }> | undefined,
): number {
  if (!buckets) return 0;
  return (
    (buckets['31-60']?.outstandingCents ?? 0) +
    (buckets['61-90']?.outstandingCents ?? 0) +
    (buckets['90+']?.outstandingCents ?? 0)
  );
}

/** v1.33: AR 超期单数 (31-60+ count) */
function bucketOverdueOrderCount(buckets: Record<string, { count: number }> | undefined): number {
  if (!buckets) return 0;
  return (
    (buckets['31-60']?.count ?? 0) + (buckets['61-90']?.count ?? 0) + (buckets['90+']?.count ?? 0)
  );
}

/** v1.33: 月环比符号 (简化: 拿不到历史数据 → 返回 null, 显示 "—") */
function monthlyDeltaSign(_totalCents: number): null {
  // v1.33 暂不实现真实月环比 (后端没暴露历史 API), 占位 null 显示 "—"
  return null;
}

/** v1.33: 节日 emoji (按 holiday key) */
function holidayEmojiFor(key: string): string {
  if (key.includes('christmas') || key.includes('new_year')) return '🎄';
  if (key.includes('valentine')) return '💝';
  if (key.includes('easter') || key.includes('spring')) return '🐣';
  if (key.includes('mother') || key.includes('women')) return '👩';
  if (key.includes('father')) return '👨';
  if (key.includes('thanksgiving')) return '🦃';
  if (key.includes('halloween')) return '🎃';
  if (key.includes('summer') || key.includes('beach')) return '🏖';
  if (key.includes('mid_autumn') || key.includes('moon')) return '🌕';
  if (key.includes('lunar') || key.includes('spring_festival') || key.includes('chinese_new_year'))
    return '🧧';
  return '🎉';
}

/** v1.33: 相对时间 (毫秒 → "刚刚/3 分钟前/2 小时前/3 天前") */
function formatRelativeTime(ms: number, nowMs: number = Date.now()): string {
  const diff = Math.max(0, nowMs - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return '刚刚';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** v1.33: 服务状态 → 颜色点 (跟 ServiceDot 区分: 纯 class 给 inline 用) */
function statusDotClass(status: 'ok' | 'degraded' | 'down' | undefined): string {
  if (status === 'down' || status === 'degraded') return 'bg-bad';
  return 'bg-good';
}

function Dashboard() {
  // === 数据 ===
  const { data: newInq, isLoading: inqLoading } = useQuery({
    queryKey: ['inquiries', 'new', { dashboard: true }],
    queryFn: () => inquiryApi.list({ status: 'new' }),
    refetchInterval: 60000,
  });
  const { data: arData, isLoading: arLoading } = useQuery({
    queryKey: ['finance', 'ar', 'summary', { dashboard: true }],
    queryFn: () => financeApi.arSummary(),
    refetchInterval: 60000,
  });
  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: ['orders', 'producing', { dashboard: true }],
    queryFn: () => orderApi.list({ status: 'producing' }),
    refetchInterval: 120000,
  });
  const { data: holidayList } = useQuery({
    queryKey: ['holidays', 'list', { dashboard: true }],
    queryFn: () => holidayApi.list(),
    staleTime: 24 * 60 * 60 * 1000,
  });
  // 也要 customers lookup, 订单卡片显示客户名
  const { data: customers } = useQuery({
    queryKey: ['customers', 'lookup', { dashboard: true }],
    queryFn: () => customerApi.list({ limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });
  const customerById = new Map((customers?.items ?? []).map((c) => [c.id, c]));

  // v1.32 P2-3: 系统健康 + 本月数据
  const { data: sysStatus } = useQuery({
    queryKey: ['system', 'status', { dashboard: true }],
    queryFn: () => systemApi.status(),
    refetchInterval: 60000,
    staleTime: 30000,
  });
  // 本月利润 + 退税 + 询盘数 (财务 + 询盘 stats)
  const { data: profitSum } = useQuery({
    queryKey: ['finance', 'profit', 'summary', { dashboard: true }],
    queryFn: () => financeApi.profitSummary('month'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: taxSum } = useQuery({
    queryKey: ['finance', 'tax-refund', 'summary', { dashboard: true }],
    queryFn: () => financeApi.taxRefundSummary(),
    staleTime: 5 * 60 * 1000,
  });

  // v1.33: AI 教练今日 session 数 (复用 coachApi.listSessions, 算 createdAt 在 today 内)
  const { data: coachHistory } = useQuery({
    queryKey: ['coach', 'sessions', { dashboard: true }],
    queryFn: () => import('../lib/api').then((m) => m.coachApi.listSessions()),
    staleTime: 60 * 1000,
  });
  const todayStart = todayStartMs();
  const coachTodayCount = (coachHistory?.sessions ?? []).filter(
    (s: CoachSession) => s.createdAt >= todayStart,
  ).length;

  // === 派生 ===
  const today = new Date();
  const greeting = greetingForDate(today);
  const weekdayZh = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][today.getDay()] ?? '';
  const dateLabel = `${today.getMonth() + 1}月${today.getDate()}日 · ${weekdayZh}`;
  const newCount = newInq?.total ?? 0;
  const todoTotal = newCount;
  const nextH = nextHolidayDiff(holidayList?.items ?? []);

  const arSummary = arData?.summary;
  const totalOutstanding = arSummary?.totalOutstandingCents ?? 0;
  const totalCurrency = arSummary?.currency ?? 'USD';
  const buckets = arSummary?.buckets;
  const b0 = buckets?.['0-30']?.outstandingCents ?? 0;
  const b30 = buckets?.['31-60']?.outstandingCents ?? 0;
  const b60_90 = buckets?.['61-90']?.outstandingCents ?? 0;
  const b90 = buckets?.['90+']?.outstandingCents ?? 0;
  const b60 = b60_90 + b90;
  const bDenom = b0 + b30 + b60 || 1;
  const p0 = Math.round((b0 / bDenom) * 100);
  const p30 = Math.round((b30 / bDenom) * 100);
  const p60 = 100 - p0 - p30;
  const overdueCents = bucketOverdueAmountCents(buckets);
  const overdueCount = bucketOverdueOrderCount(buckets);
  const monthlyDelta = monthlyDeltaSign(totalOutstanding);

  // v1.33: 订单 — 工厂待发 (装船前) + 客户待回款 (装船后) 计数
  // 注意: 现有 orderApi.list({ status: 'producing' }) 只拉 producing;
  // dashboard 顶部需要 ALL active orders 才能算 factory/postship 拆开 — 复用 list() 无 filter
  const { data: allActiveOrders } = useQuery({
    queryKey: ['orders', 'active-all', { dashboard: true }],
    queryFn: () => orderApi.list(),
    refetchInterval: 120000,
  });
  const activeOrderItems = (allActiveOrders?.items ?? []).filter(
    (o) => o.status !== 'closed' && o.status !== 'cancelled',
  );
  const factoryOrderCount = orderFactoryStages(activeOrderItems).length;
  const postshipOrderCount = orderPostshipStages(activeOrderItems).length;
  const activeOrders = (orders?.items ?? []).slice(0, 3);

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* === 顶: 问候 + 今日待办 === */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs text-ink-3 mb-1 flex items-center gap-1.5">
            <Icon name="sun" size="xs" className="text-warn" />
            今日 {dateLabel}
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight">
            {greeting}, <span className="text-accent">小肥</span> ☕
          </h1>
          <p className="text-xs sm:text-sm text-ink-3 mt-1">欢迎回到 Trade OS · v1.33 今日工作台</p>
        </div>
        <div className="flex items-center gap-2">
          {todoTotal > 0 ? (
            <output
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full bg-bad/10 text-bad border border-bad/30"
              aria-label={`今日 ${todoTotal} 条待办`}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-bad opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-bad" />
              </span>
              今日 {todoTotal} 条待办
            </output>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full bg-good/10 text-good border border-good/30">
              <Icon name="check" size="xs" strokeWidth={2.5} />
              今日已清空 ✨
            </span>
          )}
        </div>
      </header>

      {/* === v1.33: Sticky 顶栏 — 系统状态点 + 3 快捷按钮 (含移动端汉堡) === */}
      <StickyTopBar sysStatus={sysStatus} />

      {/* === v1.32 P2-3: 关键 metrics === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="今日询盘"
          value={`${newCount} 条`}
          sub={newCount > 0 ? '待处理' : '已清空'}
          icon="mail"
          tone={newCount > 5 ? 'bad' : newCount > 0 ? 'warn' : 'good'}
          href="/inquiries"
        />
        <KpiCard
          label="本月订单"
          value={`${allActiveOrders?.total ?? 0} 笔`}
          sub={`生产中 ${factoryOrderCount}`}
          icon="list-checks"
          tone="default"
          href="/orders"
        />
        <KpiCard
          label="本月利润"
          value={
            profitSum?.items?.[0]?.profitCents !== undefined
              ? fmtMoneyCompact(
                  profitSum.items[0].profitCents,
                  profitSum.items[0].currency ?? 'USD',
                )
              : '—'
          }
          sub="预估"
          icon="circle-dollar-sign"
          tone={(profitSum?.items?.[0]?.profitCents ?? 0) > 0 ? 'good' : 'default'}
          href="/finance/profit"
        />
        <KpiCard
          label="本月退税"
          value={
            taxSum?.items?.[0]?.totalRefundCents !== undefined
              ? fmtMoneyCompact(taxSum.items[0].totalRefundCents, taxSum.items[0].currency ?? 'USD')
              : '—'
          }
          sub={`${taxSum?.items?.[0]?.orderCount ?? 0} 单`}
          icon="trending-up"
          tone="default"
          href="/finance/tax-refund"
        />
      </div>

      {/* === 4 大块: 2x2 桌面 / 1 列移动 === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card 1 — 今日询盘 (v1.33 加 7d 均 + AI 起草状态) */}
        <Card padding="md" className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="mail" size="sm" className="text-accent" />
              今日询盘
            </h2>
            <Link
              to="/inquiries"
              className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-0.5"
            >
              看全部 {(newInq?.total ?? newCount).toString()} 条
              <Icon name="arrow-right" size="xs" aria-hidden />
            </Link>
          </div>
          {inqLoading ? (
            <div className="space-y-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : (newInq?.items.length ?? 0) === 0 ? (
            <p className="text-xs text-ink-3 py-6 text-center">📭 暂无新询盘</p>
          ) : (
            <>
              {/* 顶: 总数 + 7d 均 (v1.33 新增) */}
              <div className="flex items-baseline gap-3 mb-2.5">
                <div>
                  <span className="text-2xl font-semibold">{newCount}</span>
                  <span className="text-[10px] text-ink-3 ml-1">条</span>
                </div>
                <div className="text-[10px] text-ink-3 flex items-center gap-1">
                  <span>7d 均</span>
                  <span className="tabular-nums">—</span>
                  <span title="v1.33 占位: 后端无 history API">(v1.34)</span>
                </div>
              </div>
              <ul className="space-y-1 flex-1">
                {(newInq?.items ?? []).slice(0, 4).map((it) => {
                  const c = it.customerId ? customerById.get(it.customerId) : undefined;
                  const draftSt = draftStatusForInquiry(inquiryDraftStatus(it.status));
                  return (
                    <li key={it.id}>
                      <Link
                        to="/inquiries/$inquiryId"
                        params={{ inquiryId: it.id }}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-2 text-sm transition-colors"
                      >
                        <span className="font-medium truncate flex-1 min-w-0">
                          {c?.name ?? '(无客户)'}
                        </span>
                        <Badge size="sm" variant={draftSt.tone} className="shrink-0">
                          <span aria-hidden>{draftSt.icon}</span>
                          {draftSt.label}
                        </Badge>
                        <span className="text-[10px] text-ink-3 shrink-0 tabular-nums">
                          {formatRelativeTime(it.receivedAt, today.getTime())}
                        </span>
                        <Icon
                          name="chevron-right"
                          size="xs"
                          className="text-ink-3 shrink-0"
                          aria-hidden
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>

        {/* Card 2 — 应收健康 (v1.33 加月环比 + 4 bucket 横条) */}
        <Card padding="md" className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="wallet" size="sm" className="text-accent" />
              应收健康
            </h2>
            <Link
              to="/finance/ar"
              className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-0.5"
            >
              看 {arSummary?.totalOrders ?? 0} 张应收
              <Icon name="arrow-right" size="xs" aria-hidden />
            </Link>
          </div>
          {arLoading ? (
            <div className="space-y-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : (
            <>
              {/* 顶: 总余额 + 月环比 (v1.33 新增) */}
              <div className="flex items-baseline gap-3 mb-2">
                <div className="text-2xl font-semibold">
                  {fmtMoneyCompact(totalOutstanding, totalCurrency)}
                </div>
                <div className="text-[10px] text-ink-3 flex items-center gap-1">
                  <span>月环比</span>
                  {monthlyDelta === null ? (
                    <span className="text-ink-3">—</span>
                  ) : (
                    <span className={monthlyDelta >= 0 ? 'text-good' : 'text-bad'}>
                      {monthlyDelta >= 0 ? '↑' : '↓'} {Math.abs(monthlyDelta)}%
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[10px] text-ink-3 mb-2.5">
                {arSummary?.totalOrders ?? 0} 笔未结清 · {totalCurrency}
              </div>
              {/* 4 bucket 横条 (v1.33: 加 61-90 + 90+ 拆分显示) */}
              <div className="space-y-1.5 flex-1">
                <ArBucketBar
                  label="0-30 天"
                  cents={b0}
                  pct={p0}
                  currency={totalCurrency}
                  count={buckets?.['0-30']?.count ?? 0}
                  tone="warn"
                />
                <ArBucketBar
                  label="31-60 天"
                  cents={b30}
                  pct={p30}
                  currency={totalCurrency}
                  count={buckets?.['31-60']?.count ?? 0}
                  tone="warn-2"
                />
                <ArBucketBar
                  label="61-90 天"
                  cents={b60_90}
                  pct={bDenom > 0 ? Math.round((b60_90 / bDenom) * 100) : 0}
                  currency={totalCurrency}
                  count={buckets?.['61-90']?.count ?? 0}
                  tone="bad"
                />
                <ArBucketBar
                  label="90+ 天"
                  cents={b90}
                  pct={bDenom > 0 ? Math.round((b90 / bDenom) * 100) : 0}
                  currency={totalCurrency}
                  count={buckets?.['90+']?.count ?? 0}
                  tone="bad-2"
                />
              </div>
              {/* 超期合计 (v1.33 新增: ⚠ badge) */}
              {overdueCents > 0 && (
                <div className="mt-2.5 flex items-center justify-between gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 text-bad font-medium">
                    <span aria-hidden>⚠️</span>
                    超期合计 (31d+)
                  </span>
                  <Link
                    to="/finance/ar"
                    search={{ bucket: '31-60' }}
                    className="inline-flex items-center gap-1 text-bad hover:underline tabular-nums"
                  >
                    {fmtMoneyCompact(overdueCents, totalCurrency)}
                    <span className="text-ink-3">·</span>
                    <span className="text-ink-3">{overdueCount} 笔</span>
                    <Icon name="arrow-right" size="xs" aria-hidden />
                  </Link>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Card 3 — 进行中订单 (v1.33 加工厂待发/客户回款 + 12 步清单) */}
        <Card padding="md" className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="list-checks" size="sm" className="text-accent" />
              进行中订单
            </h2>
            <Link
              to="/orders"
              className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-0.5"
            >
              看全部 {allActiveOrders?.total ?? 0} 单
              <Icon name="arrow-right" size="xs" aria-hidden />
            </Link>
          </div>
          {ordersLoading ? (
            <div className="space-y-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : activeOrders.length === 0 ? (
            <p className="text-xs text-ink-3 py-6 text-center">📦 暂无生产中订单</p>
          ) : (
            <>
              {/* 顶: 总数 + 工厂待发/客户回款 (v1.33 新增) */}
              <div className="flex items-baseline gap-3 mb-2.5">
                <div>
                  <span className="text-2xl font-semibold">{allActiveOrders?.total ?? 0}</span>
                  <span className="text-[10px] text-ink-3 ml-1">单</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-info/10 text-info border border-info/30">
                    工厂待发
                    <span className="tabular-nums font-semibold">{factoryOrderCount}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-warn/10 text-warn border border-warn/30">
                    客户回款
                    <span className="tabular-nums font-semibold">{postshipOrderCount}</span>
                  </span>
                </div>
              </div>
              <ul className="space-y-2 flex-1">
                {activeOrders.map((o) => {
                  const c = customerById.get(o.customerId);
                  const total = o.checklistProgress.totalSteps || 12;
                  const done = o.checklistProgress.completedSteps;
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  return (
                    <li key={o.id}>
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: o.id }}
                        className="block px-2.5 py-2 rounded hover:bg-surface-2 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-sm font-medium truncate">
                            {o.poNumber || o.id.slice(0, 8)}
                          </span>
                          <span className="text-[10px] text-ink-3 shrink-0 tabular-nums">
                            {done}/{total} 步 · {pct}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-xs text-ink-3">
                          <span className="truncate">{c?.name ?? '(无客户)'}</span>
                          <span className="shrink-0">{o.incoterm}</span>
                        </div>
                        <div className="h-1 bg-surface-2 rounded overflow-hidden mt-1.5">
                          <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>

        {/* Card 4 — 节日倒计时 (v1.33 加 24h 内 pulse + 24h 计数) */}
        <Card padding="md" className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <Icon name="trees" size="sm" className="text-accent" />
              节日倒计时
            </h2>
            <Link
              to="/outbound"
              className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-0.5"
            >
              节日营销
              <Icon name="arrow-right" size="xs" aria-hidden />
            </Link>
          </div>
          {!nextH ? (
            <p className="text-xs text-ink-3 py-6 text-center">⏳ 加载节日列表…</p>
          ) : (
            <>
              {/* 顶: 大数字 + 节日 emoji (v1.33) */}
              <div className="flex items-baseline gap-2 mb-1">
                {nextH.days <= 1 ? (
                  <span className="text-4xl font-bold text-bad leading-none animate-pulse">
                    {nextH.days === 0 ? '今天!' : '明天!'}
                  </span>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-accent leading-none">
                      {nextH.days}
                    </span>
                    <span className="text-sm text-ink-3">天后</span>
                  </>
                )}
                <span className="text-2xl ml-auto" aria-hidden>
                  {holidayEmojiFor(nextH.key)}
                </span>
              </div>
              <div className="text-base font-medium mb-1">{nextH.name}</div>
              {/* 中: 倒计时文案 (v1.33 新增: 准备清单) */}
              <div className="text-xs text-ink-2 mb-3">
                {nextH.days === 0 ? (
                  <span className="text-bad font-medium">🎉 今天节日, 客户问候别错过</span>
                ) : nextH.days === 1 ? (
                  <span className="text-warn font-medium">⏰ 明天节日, 准备发邮件</span>
                ) : nextH.days <= 7 ? (
                  <span>
                    还 {nextH.days} 天, 准备
                    <span className="text-good ml-1">✓ 模板</span>
                    <span className="text-ink-3 ml-1">✗ 客户列表</span>
                  </span>
                ) : (
                  <span className="text-ink-3">
                    还 {nextH.days} 天, 准备
                    <span className="text-good ml-1">✓ 模板</span>
                    <span className="text-ink-3 ml-1">✗ 客户列表</span>
                  </span>
                )}
              </div>
              {/* 24h 内节日: pulse 红色 + 计数 (v1.33) */}
              {nextH.days <= 1 && (
                <div className="mb-2 px-2 py-1.5 bg-bad/10 border border-bad/30 rounded text-xs text-bad flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-bad opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-bad" />
                  </span>
                  <span className="font-medium">今天发 0 封</span>
                  <span className="text-ink-3 ml-1">(v1.34 实接发件 API)</span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="send"
                  className="flex-1"
                  onClick={() => {
                    // 跳 /outbound 让用户走 wizard
                    window.location.href = '/outbound';
                  }}
                >
                  一键发邮件
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft="eye"
                  onClick={() => {
                    window.location.href = '/outbound';
                  }}
                >
                  预览模板
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* === v1.33: 第 5 块 — AI 教练 + 5 快捷 (1/3 + 2/3 桌面 / 1 列移动) === */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 5 — AI 教练 (新) */}
        <Card padding="md" className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <span className="text-base" aria-hidden>
                🤖
              </span>
              AI 教练
            </h2>
            <span className="text-[10px] text-ink-3 tabular-nums">v1.32+</span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-semibold">{coachTodayCount}</span>
            <span className="text-[10px] text-ink-3">今日对话</span>
          </div>
          <p className="text-[10px] text-ink-3 mb-3 leading-relaxed">
            训练反骗 · 实时评分 · 实战模拟
          </p>
          <Link to="/coach" className="block mt-auto" aria-label="跳到 AI 教练">
            <Button variant="primary" size="sm" iconLeft="message-square" block>
              打开教练
            </Button>
          </Link>
          <Link
            to="/coach"
            className="mt-1.5 text-[10px] text-ink-3 hover:text-accent inline-flex items-center gap-0.5 justify-end"
          >
            看训练历史
            <Icon name="arrow-right" size="xs" aria-hidden />
          </Link>
        </Card>

        {/* v1.33: 5 快捷操作 (从 1/1 缩为 2/3 桌面) */}
        <Card padding="md" variant="bordered" className="md:col-span-2 flex flex-col">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="zap" size="sm" className="text-accent" />
            <h2 className="text-sm font-semibold">快速操作</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 flex-1">
            <QuickAction to="/customers/new" icon="user-plus" label="加客户" />
            <QuickAction to="/inquiries" icon="mail" label="录询盘" />
            <QuickAction to="/outbound" icon="gift" label="节日邮件" />
            <QuickAction to="/settings" icon="key" label="改密码" />
            <QuickAction to="/finance" icon="circle-dollar-sign" label="看财务" />
          </div>
        </Card>
      </div>
    </div>
  );
}

// === 子组件 ===

/** v1.33: AR bucket 横条 (加 count 字段) */
function ArBucketBar({
  label,
  cents,
  pct,
  currency,
  count,
  tone,
}: {
  label: string;
  cents: number;
  pct: number;
  currency: string;
  count: number;
  tone: 'warn' | 'warn-2' | 'bad' | 'bad-2';
}) {
  const colorClass =
    tone === 'warn'
      ? 'bg-warn'
      : tone === 'warn-2'
        ? 'bg-warn/80'
        : tone === 'bad'
          ? 'bg-bad'
          : 'bg-bad/80';
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] mb-0.5">
        <span className="text-ink-2">
          {label} <span className="text-ink-3">({count})</span>
        </span>
        <span className="font-medium tabular-nums">
          {fmtMoneyCompact(cents, currency)} · {pct}%
        </span>
      </div>
      <div
        className="h-1.5 bg-surface-2 rounded overflow-hidden"
        role="progressbar"
        tabIndex={0}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} 占比 ${pct}%`}
      >
        <div
          className={`h-full ${colorClass} transition-all duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** v1.32 P2-3: 关键指标卡 (4 列) */
function KpiCard({
  label,
  value,
  sub,
  icon,
  tone,
  href,
}: {
  label: string;
  value: string;
  sub: string;
  icon: string;
  tone: 'default' | 'good' | 'warn' | 'bad';
  href?: string;
}) {
  const toneRing =
    tone === 'bad' ? 'border-bad/30' : tone === 'warn' ? 'border-warn/30' : 'border-line';
  const inner = (
    <div className={`bg-surface border ${toneRing} rounded p-3 hover:shadow-sm transition-shadow`}>
      <div className="flex items-center gap-1.5 text-[10px] text-ink-3 mb-1">
        <Icon name={icon} size="xs" aria-hidden />
        {label}
      </div>
      <div className="text-lg sm:text-xl font-semibold tabular-nums truncate">{value}</div>
      <div className="text-[10px] text-ink-3 mt-0.5">{sub}</div>
    </div>
  );
  if (href) {
    return (
      <Link to={href} className="block">
        {inner}
      </Link>
    );
  }
  return inner;
}

/** v1.33: Sticky 顶栏 — 系统状态点 + 3 快捷按钮 + 移动端汉堡弹层 */
function StickyTopBar({
  sysStatus,
}: { sysStatus: import('../lib/api').SystemStatusInfo | undefined }) {
  const [menuOpen, setMenuOpen] = useState(false);
  // 计算整体健康 (任一服务 down → 整体红)
  const allOk =
    sysStatus &&
    sysStatus.api.status === 'ok' &&
    sysStatus.services.database.status === 'ok' &&
    sysStatus.services.storage.status === 'ok' &&
    sysStatus.services.ai.status === 'ok';
  const healthLabel = !sysStatus ? '检测中…' : allOk ? '全部正常' : '服务异常';
  const healthTone = allOk ? 'good' : sysStatus ? 'bad' : 'warn';

  return (
    <div className="sticky top-14 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-1.5 bg-surface/85 backdrop-blur border-b border-line/60">
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        {/* 系统健康 (紧凑) */}
        <div
          className="flex items-center gap-1.5 text-[10px] sm:text-xs"
          aria-label={`系统健康: ${healthLabel}`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              healthTone === 'good' ? 'bg-good' : healthTone === 'bad' ? 'bg-bad' : 'bg-warn'
            }`}
            aria-hidden
          />
          <span className="text-ink-2 font-medium">{healthLabel}</span>
          <span className="text-ink-3">·</span>
          {sysStatus && (
            <>
              <ServiceDot
                label="API"
                status={sysStatus.api.status}
                latency={sysStatus.api.latencyMs}
              />
              <span className="text-ink-3">·</span>
              <ServiceDot
                label="D1"
                status={sysStatus.services.database.status}
                latency={sysStatus.services.database.latencyMs}
              />
              <span className="text-ink-3 hidden sm:inline">·</span>
              <ServiceDot
                label="R2"
                status={sysStatus.services.storage.status}
                latency={sysStatus.services.storage.latencyMs}
                className="hidden sm:inline-flex"
              />
              <span className="text-ink-3 hidden sm:inline">·</span>
              <ServiceDot
                label="AI"
                status={sysStatus.services.ai.status}
                latency={sysStatus.services.ai.latencyMs}
                className="hidden sm:inline-flex"
              />
            </>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          {/* 桌面 3 快捷按钮 */}
          <div className="hidden sm:flex items-center gap-1.5">
            <Link to="/inquiries" aria-label="新询盘">
              <Button variant="secondary" size="sm" iconLeft="mail">
                新询盘
              </Button>
            </Link>
            <Link to="/customers/new" aria-label="加客户">
              <Button variant="secondary" size="sm" iconLeft="user-plus">
                + 客户
              </Button>
            </Link>
            <Link to="/outbound" aria-label="发节日邮件">
              <Button variant="ghost" size="sm" iconLeft="gift">
                发节日
              </Button>
            </Link>
          </div>
          {/* 移动端: 2 按钮 + "..." 弹层 */}
          <div className="flex sm:hidden items-center gap-1.5">
            <Link to="/inquiries" aria-label="新询盘">
              <Button variant="secondary" size="sm" iconLeft="mail">
                新询盘
              </Button>
            </Link>
            <Link to="/customers/new" aria-label="加客户">
              <Button variant="secondary" size="sm" iconLeft="user-plus">
                + 客户
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-ink-2 hover:bg-surface-2"
              aria-label="更多操作"
              aria-expanded={menuOpen}
            >
              <Icon name="menu" size="sm" aria-hidden />
            </button>
          </div>
        </div>
        {sysStatus?.build?.version && (
          <span className="text-[10px] text-ink-3 hidden md:inline">
            v{sysStatus.build.version}
          </span>
        )}
      </div>
      {/* 移动端弹层 */}
      {menuOpen && (
        <div className="sm:hidden absolute right-4 top-full mt-1 bg-surface border border-line rounded-md shadow-md z-30 min-w-[160px] py-1">
          <Link
            to="/outbound"
            className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-surface-2"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="gift" size="sm" className="text-accent" aria-hidden />
            发节日
          </Link>
          <Link
            to="/settings"
            className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-surface-2"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="settings" size="sm" className="text-ink-2" aria-hidden />
            设置
          </Link>
          <Link
            to="/finance"
            className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-surface-2"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="circle-dollar-sign" size="sm" className="text-ink-2" aria-hidden />
            财务
          </Link>
        </div>
      )}
    </div>
  );
}

// v1.32 P2-3: 服务健康点 (v1.33: 加 className 用于移动端隐藏)
function ServiceDot({
  label,
  status,
  latency,
  className,
}: {
  label: string;
  status: 'ok' | 'degraded' | 'down';
  latency?: number;
  className?: string;
}) {
  const color = status === 'ok' ? 'bg-good' : 'bg-bad';
  return (
    <span
      className={`inline-flex items-center gap-1 ${className ?? ''}`}
      title={latency !== undefined ? `${label}: ${latency}ms` : label}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} aria-hidden />
      <span>{label}</span>
      {latency !== undefined && <span className="text-ink-3 ml-0.5 tabular-nums">{latency}ms</span>}
    </span>
  );
}

function QuickAction({
  to,
  icon,
  label,
}: {
  to: string;
  icon: string;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center justify-center gap-1.5 p-3 sm:p-4 rounded-md border border-line bg-surface hover:border-accent hover:bg-accent-soft/50 transition-colors text-center min-h-[72px]"
    >
      <Icon name={icon} size="md" className="text-accent" aria-hidden />
      <span className="text-xs sm:text-sm text-ink-2 font-medium">{label}</span>
    </Link>
  );
}
